/**
 * Action buttons component for SpeakingInterface
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mic, Square, Loader2, CheckCircle2, AlertCircle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SpeakingMode } from './SpeakingInterface.helpers';
import { getSkipButtonText } from './SpeakingInterface.helpers';
import { ROUTES } from '@/lib/routeConstants';

type SaveStatus = 'idle' | 'saving' | 'retrying' | 'saved' | 'error';
type PermissionStatus = 'unknown' | 'granted' | 'denied' | null;

interface ActionButtonsProps {
    readonly mode: SpeakingMode;
    readonly saveStatus: SaveStatus;
    readonly saveError: string | null;
    readonly retryCount: number;
    readonly permissionStatus: PermissionStatus;
    readonly isSaving: boolean;
    readonly voiceConsentDenied?: boolean;
    readonly onStartEarly: () => void;
    readonly onStopRecording: () => void;
    readonly onRetrySave: () => void;
}

export function ActionButtons({
    mode,
    saveStatus,
    saveError,
    retryCount,
    permissionStatus,
    isSaving,
    voiceConsentDenied = false,
    onStartEarly,
    onStopRecording,
    onRetrySave,
}: ActionButtonsProps) {
    const { t } = useTranslation();

    const canStartEarly = mode === 'prep' || mode === 'context-audio' || mode === 'section-audio';
    const isRecording = mode === 'recording';
    const isProcessing = mode === 'processing';

    if (voiceConsentDenied) {
        return (
            <div className="flex flex-col items-center gap-3 text-center">
                <Button size="lg" disabled className="w-full sm:w-auto px-8 h-14 sm:h-16 opacity-50 cursor-not-allowed" aria-disabled="true">
                    <Mic className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    {t('simulation.speaking.startRecording', { defaultValue: 'Start Recording' })}
                </Button>
                <p className="text-sm text-muted-foreground max-w-sm">
                    {t('consent.simulation.voiceDisabledMessage', {
                        defaultValue: 'Because of your privacy settings, this option is not available. To use this option, go to',
                    })}{' '}
                    <Link to={`${ROUTES.SETTINGS}#privacy`} className="underline text-primary">
                        {t('consent.simulation.voiceDisabledLink', { defaultValue: 'Settings' })}
                    </Link>{' '}
                    {t('consent.simulation.voiceDisabledSuffix', {
                        defaultValue: 'and update your privacy preferences.',
                    })}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            {canStartEarly && (
                <Button
                    size="lg"
                    onClick={onStartEarly}
                    className="w-full sm:w-auto bg-exam-prep hover:bg-exam-prep/90 text-white px-8 h-14 sm:h-16 text-sm sm:text-base"
                    disabled={permissionStatus === 'denied' || isSaving}
                >
                    <Mic className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    {getSkipButtonText(mode, t)}
                </Button>
            )}

            {isRecording && (
                <Button
                    size="lg"
                    onClick={onStopRecording}
                    variant="destructive"
                    className="w-full sm:w-auto bg-exam-recording hover:bg-exam-recording/90 px-8 h-14 sm:h-16 text-sm sm:text-base"
                    disabled={isSaving}
                >
                    <Square className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    {t('simulation.speaking.stopRecording', { defaultValue: 'Stop Recording' })}
                </Button>
            )}

            {isProcessing && (
                <div className="flex items-center justify-center gap-2 text-muted-foreground py-2">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm sm:text-base font-ui">{t('simulation.active.savingResponse', { defaultValue: 'Saving response...' })}</span>
                </div>
            )}

            <SaveStatusDisplay
                saveStatus={saveStatus}
                saveError={saveError}
                retryCount={retryCount}
                isSaving={isSaving}
                onRetrySave={onRetrySave}
            />
        </div>
    );
}

interface SaveStatusDisplayProps {
    readonly saveStatus: SaveStatus;
    readonly saveError: string | null;
    readonly retryCount: number;
    readonly isSaving: boolean;
    readonly onRetrySave: () => void;
}

function SaveStatusDisplay({ saveStatus, saveError, retryCount, isSaving, onRetrySave }: SaveStatusDisplayProps) {
    const { t } = useTranslation();

    if (saveStatus === 'saving') {
        return (
            <div className="flex items-center justify-center gap-2 text-exam-prep py-2">
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                <span className="text-sm sm:text-base font-ui">{t('simulation.active.savingRecordings', { defaultValue: 'Saving recordings...' })}</span>
            </div>
        );
    }

    if (saveStatus === 'retrying') {
        return (
            <div className="flex items-center justify-center gap-2 text-exam-prep py-2">
                <RotateCw className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                <span className="text-sm sm:text-base font-ui">
                    {saveError || t('simulation.active.retrying', { attempt: retryCount + 1, total: 3, defaultValue: `Retrying... (${retryCount + 1}/3)` })}
                </span>
            </div>
        );
    }

    if (saveStatus === 'saved') {
        return (
            <div className="flex items-center justify-center gap-2 text-exam-success py-2">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base font-ui">{t('simulation.active.recordingsSaved', { defaultValue: 'Saved successfully' })}</span>
            </div>
        );
    }

    if (saveStatus === 'error') {
        return (
            <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-base font-ui">{t('simulation.errors.saveRecordingsFailed', { defaultValue: 'Save failed' })}</span>
                </div>
                {saveError && (
                    <p className="text-[10px] sm:text-xs text-muted-foreground text-center font-ui max-w-md">
                        {saveError}
                    </p>
                )}
                <Button
                    size="lg"
                    onClick={onRetrySave}
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white px-8 h-10 sm:h-auto text-sm"
                    disabled={isSaving}
                >
                    <RotateCw className="w-4 h-4 mr-2" />
                    {t('simulation.active.retrySave', { defaultValue: 'Retry Save' })}
                </Button>
            </div>
        );
    }

    return null;
}
